import React from 'react';
import {
  Box,
  Paper,
  IconButton,
  Divider,
  useMediaQuery,
  Typography,
  Stack,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AiFillHome, AiFillCaretRight } from 'react-icons/ai';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import Card from '@mui/material/Card';
import { ChevronRight } from '@mui/icons-material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { useDispatch, useSelector } from 'react-redux';
import { SET_MENU } from '../../store/actions';
import NeedHelpButton from 'common-components/NeedHelpButton';
import ImageAvatar from 'ui-components/ImageAvatar';
import ProgressBar from './ProgressBar';
import SIDEBAR_CONFIG from '../Sidebar/sidebarConfig';

const BreadcrumbHeader = ({
  title,
  titleStyle = {},
  shouldGoBack = false,
  breadcrumbTitle = '',
  Actions = () => <NeedHelpButton />,
  customBackgroundColor,
  sx = {},
}) => {
  const location = useLocation().pathname;
  const otherData = useSelector((state) => state?.permissions?.otherData);
  const routes = [
    {
      path: '/',
      children: [
        { path: '', breadcrumb: title },
        {
          path: 'update',
          breadcrumb: () => (
            <Box
              sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}
            >
              {/* this crumb draws its own parent, so it has to be hidden on
                  phones as well */}
              {!isSmallScreen && (
                <>
                  <Link to={'/'} style={{ textDecoration: 'none' }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' },
                        fontWeight: 700,
                        color: theme.palette.customColors.indigo600,
                        width: 'max-content',
                      }}
                    >
                      {breadcrumbTitle}
                    </Typography>
                  </Link>

                  <ChevronRight
                    sx={{ fontSize: '1.5rem', color: theme.palette.secondary[800] }}
                    style={{ marginTop: '4px' }}
                  />
                </>
              )}
              <Typography
                variant="title"
                sx={{
                  textDecoration: 'none',
                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' },
                  margin: 0,
                  color: theme.palette.secondary[800],
                  fontWeight: 700,
                  width: 'max-content',
                }}
              >
                {title}
              </Typography>
            </Box>
          ),
        },
      ],
    },
    {
      path: 'profile',
      children: [
        {
          path: '',
          breadcrumb: location?.startsWith('/profile/update')
            ? breadcrumbTitle
            : title,
        },
        { path: 'update', breadcrumb: title },
        // The profile tabs are segments of the same page, so they must not add a
        // crumb - without this they fall through to the `*` route below and draw
        // the page title (and its icon row) a second time.
        { path: 'views', breadcrumb: null },
      ],
    },
    {
      path: 'rounds',
      breadcrumb: 'All Rounds',
      children: [
        {
          path: 'create_round',
          breadcrumb: 'Add',
          children: [
            { path: ':id', breadcrumb: null }, // Breadcrumb for :id will not be rendered
          ],
        },
        { path: 'edit_round', breadcrumb: title },
        // { path: 'edit_round/:id', breadcrumb: title },
        { path: '*', breadcrumb: title },
        // { path: 'view/:id', breadcrumb: title },
      ],
    },
    {
      path: 'active-round',
      breadcrumb: 'Open Round',
      children: [{ path: '*', breadcrumb: null }],
    },
    // {
    //     path: 'reports',
    //     breadcrumb: 'Reports',
    //     children: [
    //         {
    //             path: 'create_report', breadcrumb: 'Add',
    //             children: [
    //                 { path: ':id', breadcrumb: null }, // Breadcrumb for :id will not be rendered
    //             ],
    //         },
    //         { path: 'edit_report', breadcrumb: title },
    //         // { path: 'edit_report/:id', breadcrumb: title },
    //         // { path: 'view', breadcrumb: null },
    //         // { path: 'view/:id', breadcrumb: title },
    //     ],
    // },
    {
      path: 'reports',
      children: [
        {
          path: '',
          breadcrumb: location?.includes('create_report')
            ? null
            : 'All Updates',
        },
        {
          path: 'create_report',
          breadcrumb: 'Create Update',
        },
        { path: 'edit_report', breadcrumb: 'Edit Update' },
        // {
        //     path: 'create_report', breadcrumb: 'Add',
        //     children: [
        //         { path: ':id', breadcrumb: null }, // Breadcrumb for :id will not be rendered
        //     ],
        // },
        // { path: 'edit_report', breadcrumb: title },
        // { path: 'edit_report/:id', breadcrumb: title },
        // { path: 'view', breadcrumb: null },
        // { path: 'view/:id', breadcrumb: title },
      ],
    },
    {
      path: 'performance',
      breadcrumb: 'Performance Metrics',
      children: [{ path: 'edit_performance', breadcrumb: title }],
    },
    {
      path: 'investors',
      breadcrumb: 'Investors',
      children: [
        {
          path: '',
          breadcrumb:
            location?.startsWith('/investors/pipeline') ||
            location?.startsWith('/investors/invested')
              ? 'Investors'
              : title,
        },
        { path: 'pipeline', breadcrumb: 'Pipeline' },
        { path: 'invested', breadcrumb: 'Invested' },
        { path: 'invested/add-investment', breadcrumb: 'Add Investment' },
        { path: 'invested/edit-investment', breadcrumb: 'Edit Investment' },
      ],
    },
    {
      path: 'captable',
      breadcrumb: 'Cap Table',
      children: [
        { path: 'add-investment', breadcrumb: 'Add Investment' },
        { path: 'edit-investment', breadcrumb: 'Edit Investment' },
      ],
    },
    {
      path: 'pipeline',
      breadcrumb: 'Investor Pipeline',
      children: [{ path: 'pipeline', breadcrumb: 'Pipeline' }],
    },
    {
      path: 'investor-network',
      breadcrumb: 'Investor Network (Matched)',
    },
    {
      path: 'settings',
      children: [
        {
          path: '',
          breadcrumb: location?.startsWith('/settings/add_user')
            ? breadcrumbTitle
            : title,
        },
        { path: 'add_user', breadcrumb: title },
      ],
    },
    {
      path: 'manage_subscription',
      children: [
        { path: '', breadcrumb: title },
        // { path: 'add_user', breadcrumb: title },
      ],
    },
    {
      path: 'orders-invoices',
      children: [
        { path: '', breadcrumb: title },
        { path: 'subscriptions', breadcrumb: 'Subscriptions' },
        { path: 'one-time', breadcrumb: 'One-Time Purchases' },
        // { path: 'add_user', breadcrumb: title },
      ],
    },
    // {
    //     path: 'drive',
    //     children: [
    //         // { path: '', breadcrumb: location?.startsWith("/drive/documents") || location?.startsWith("/drive/data-rooms") ? breadcrumbTitle : title },
    //         { path: '', breadcrumb: null },
    //         { path: 'documents', breadcrumb: title },
    //         { path: 'data-rooms', breadcrumb: location?.startsWith("/drive/data-rooms/detail") ? "My Data Rooms" : "My Data Rooms" },
    //         { path: 'data-rooms/detail', breadcrumb: title },
    //         // { path: 'add_user', breadcrumb: title },
    //     ],
    // },
    // { path: '', breadcrumb: location?.startsWith("/drive/documents") || location?.startsWith("/drive/data-rooms") ? breadcrumbTitle : title },
    { path: '', breadcrumb: null },
    { path: 'documents', breadcrumb: title },
    {
      path: 'data-rooms',
      breadcrumb: location?.startsWith('/drive/data-rooms/detail')
        ? 'All Data Rooms'
        : 'All Data Rooms',
    },
    { path: 'data-rooms/detail', breadcrumb: title },
    // { path: 'add_user', breadcrumb: title },
    {
      path: '*',
      breadcrumb: title,
    },
  ];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isNotLargeScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const open = useSelector((state) => state.customization.opened);
  const dispatch = useDispatch();
  const drawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !open });
  };

  const breadcrumbs = useBreadcrumbs(routes);
  // Phones only have room for the page's own heading, so the parent trail is
  // dropped there and just the last crumb is rendered.
  const renderedCrumbs = React.useMemo(() => {
    const named = breadcrumbs?.filter(({ breadcrumb }) => Boolean(breadcrumb)) ?? [];
    return isSmallScreen ? named.slice(-1) : named;
  }, [breadcrumbs, isSmallScreen]);
  // console.log(otherData,"otherData")
  return (
    <>
      {/* {shouldGoBack && <IconButton sx={{backgroundColor: '#fff', boxShadow: 3}} onClick={()=>console.log(navigate(-1))}>
        <KeyboardBackspaceIcon />
      </IconButton>} */}
      <Card
        sx={{
          padding: { xs: '1rem 1rem 0rem 1rem', sm: '1rem 2rem 0rem 2rem' },
          boxShadow: 'none',
          borderRadius: { xs: 0, sm: '24px 0 0 0' },
          background: customBackgroundColor
            ? customBackgroundColor
            : "#fffdfa",
          ...sx,
        }}
      >
        <Box
          sx={{
            backgroundColor: customBackgroundColor
              ? customBackgroundColor
              : "#fffdfa",
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flex: 1,
            maxHeight: 30,
          }}
          className="breadcrumb-flex-wrap"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: { xs: 0, sm: 1 },
            }}
            className="breadcrumb-box"
          >
            {/* <Link to="/" style={{ textDecoration: 'none' }}>
                            <AiFillHome
                                style={{ position: 'relative', top: '2px', fontSize: '16px' }}
                                color={theme.palette.secondary[800]}
                            />
                        </Link> */}
            {isNotLargeScreen && (
              <IconButton
                sx={{
                  padding: 0,
                  display: 'grid',
                  placeItems: 'center',
                  marginRight: 1,
                }}
                onClick={drawerToggle}
              >
                {' '}
                <MenuRoundedIcon
                  sx={{ width: '2.5rem', height: '2.5rem', padding: '5px' }}
                />{' '}
              </IconButton>
            )}
            {renderedCrumbs?.map(({ match, breadcrumb, key, location }, index) => {
              const isLast = renderedCrumbs.length - 1 === index;

              return (
                <Box key={key}>
                  {location.pathname !== key ? (
                    <>
                      {(index > 0 || isSmallScreen) && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {isLast ? (
                            <Typography
                              variant="title"
                              sx={{
                                textDecoration: 'none',
                                fontSize: {
                                  xs: '1rem',
                                  sm: '1.25rem',
                                  md: '1.25rem',
                                },
                                margin: 0,
                                color: theme.palette.secondary[800],
                                fontWeight: 700,
                                width: 'max-content',
                              }}
                            >
                              {breadcrumb}
                            </Typography>
                          ) : (
                            <Link
                              to={match.pathname}
                              style={{ textDecoration: 'none' }}
                            >
                              <Typography
                                sx={{
                                  fontSize: {
                                    xs: '1rem',
                                    sm: '1.25rem',
                                    md: '1.25rem',
                                  },
                                  fontWeight: 700,
                                  color: theme.palette.customColors.indigo600,
                                  width: 'max-content',
                                }}
                              >
                                {breadcrumb}
                              </Typography>
                            </Link>
                          )}
                          {!isLast && (
                            <ChevronRight
                              sx={{
                                fontSize: '1.5rem',
                                color: theme.palette.secondary[800],
                              }}
                              style={{ marginTop: '4px' }}
                              // color={theme.palette.secondary[800]}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography
                      sx={{
                        textDecoration: 'none',
                        fontSize: { xs: '1rem', sm: '1.25rem', md: '1.25rem' },
                        margin: 0,
                        color: theme.palette.secondary[800],
                        fontWeight: 700,
                        width: 'max-content',
                        ...titleStyle,
                      }}
                    >
                      {breadcrumb}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
          {/* <ProgressBar /> */}
          <Box
            sx={{
              display: 'flex',
              flex: { xs: 0, sm: 1 },
              alignItems: 'center',
              position: isNotLargeScreen ? 'auto' : 'auto',
              bottom: isNotLargeScreen ? 'auto' : 'auto',
              zIndex: 3,
              right: '1rem',
              justifyContent: 'flex-end',
            }}
          >
            <ProgressBar />
            <Actions />
            {otherData?.website_logo_url && otherData?.orgName && (
              <ImageAvatar
                src={otherData?.website_logo_url}
                alt={otherData?.orgName}
                height={45}
                width={45}
                style={{
                  borderRadius: '8px',
                  marginLeft: '0.5rem',
                }}
                tooltip={true}
              />
            )}
          </Box>
        </Box>
        <Divider
          sx={{
            marginTop: '1rem',
            background: '#EFEFF2 !important',
            opacity: 1,
          }}
        />
      </Card>
    </>
  );
};

export default BreadcrumbHeader;
